/**
 * Build a CDN URL for a heavy media asset (mp4, large webp) hosted on
 * R2 and served via the worker's `GET /media/*` route.
 *
 * Why a helper:
 *   • Keeps the production origin in one place (single source of truth).
 *   • Lets us swap to a custom media domain (e.g. `media.abdallah-issa.com`)
 *     without touching every component.
 *   • Local dev still hits `api.abdallah-issa.com/media/*` so previews
 *     match production exactly.
 *
 * Heavy assets (videos, large hero loops) live on R2 to keep the
 * Cloudflare Pages bundle slim. Small icons, illustrations, and
 * fingerprinted build artifacts stay in `public/`.
 */

// `VITE_API_URL` typically ends with `/api` (e.g. `…/api`). The worker
// serves `/media/*` on the root, NOT under `/api`, so strip any trailing
// `/api` before composing the media URL. Otherwise we'd build broken
// `…/api/media/<key>` URLs that 404.
const RAW_API_URL = (import.meta.env.VITE_API_URL ?? 'https://api.abdallah-issa.com').replace(/\/$/, '')
const API_ORIGIN = RAW_API_URL.replace(/\/api$/, '')

/**
 * Build a `/media/<key>` URL on the API origin.
 * `key` should be the R2 object key without a leading slash.
 *
 * @example
 * mediaUrl('loops/error-loop.mp4')
 * // → 'https://api.abdallah-issa.com/media/loops/error-loop.mp4'
 */
export function mediaUrl(key: string): string {
  const trimmed = key.replace(/^\/+/, '')
  return `${API_ORIGIN}/media/${trimmed}`
}

/**
 * Build a Zatoona image URL stored on R2.
 *
 * Layout convention: `r2://abdallah-issa-images/zatoona/<category>/<filename>.webp`
 * — every reference within a category has its variation file named
 * after its slug (`fish-eye.webp`, `low-angle.webp`, etc.). The category
 * canonical/baseline image lives at `_canonical.webp`.
 *
 * @example
 * zatoonaImage('camera-angles', 'fish-eye.webp')
 * // → 'https://api.abdallah-issa.com/media/zatoona/camera-angles/fish-eye.webp'
 *
 * @example
 * zatoonaImage('camera-angles', '_canonical.webp')
 * // → '...zatoona/camera-angles/_canonical.webp'
 */
export function zatoonaImage(category: string, filename: string): string {
  return mediaUrl(`zatoona/${category}/${filename}`)
}
